import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@services/newApi/typed";
import { pickArray } from "@services/normalizers";
import { queryKeys } from "@lib/queryKeys";
import type {
  IGetLogsAllParams,
  IGetLogsAllResponse,
  IGetUserByEmailAndHospitalParams,
  IGetUserByEmailAndHospitalResponse,
  IGetUserDoctorResponse,
  IGetUserResponse,
  IGetUsersByHospitalsUserParams,
  IGetUsersByHospitalsUserResponse,
  IGetUsersParams,
  IGetUsersResponse,
} from "../types";
import type { AxiosResponse } from "axios";

/**
 * Hooks react-query para o domínio Users (queries).
 *
 * Substituem os métodos imperativos que antes viviam em `contexts/Users/utils.ts`.
 * Padrão estabelecido em Task 4A (Hospitals): `useQuery` (não Suspense),
 * `enabled: false` permitido para fluxos imperativos via `refetch()`.
 */

interface IUseQueryOptions {
  enabled?: boolean | undefined;
}

/**
 * O 403 é a única negativa que o `PageErrorBoundary` sabe traduzir (aviso +
 * redirect). Ligar `throwOnError` no client inteiro quebraria o tratamento
 * gracioso de `FORBIDDEN_AT_HOSPITAL` (o interceptor de `newApi/api.ts` abre o
 * seletor de unidade), por isso mora aqui, consulta a consulta.
 */
const isForbidden = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 403;

/** GET /users — lista simples (MasterManagement metrics). */
export const useUsersListQuery = (
  params?: IGetUsersParams,
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.simpleList(params),
    queryFn: () => apiGet<IGetUsersResponse>("/users", { params }),
    enabled: options.enabled ?? true,
    staleTime: 1000 * 60 * 5,
  });

/** GET /users/:id — detalhe de um usuário. */
export const useUserDetailQuery = (
  userId: number | undefined,
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.detail(userId ?? 0),
    queryFn: () => apiGet<IGetUserResponse>(`/users/${userId}`),
    enabled: (options.enabled ?? true) && !!userId,
    staleTime: 1000 * 60 * 5,
    // Recusa de leitura tem de chegar ao boundary. Sem isto o consumidor lê
    // `data: undefined` e desenha a ficha vazia — a tela AFIRMA que não há
    // dado onde houve negativa. Sem retry: 403 não melhora repetindo.
    throwOnError: true,
    retry: false,
  });

/** GET /users/:id/logs — logs de um usuário. Boundary normaliza `{ logs }` → `{ User: { logs } }`. */
export const useUserLogsQuery = (
  userId: number | undefined,
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.userLogs(userId ?? 0),
    queryFn: async () => {
      const body = await apiGet<any>(`/users/${userId}/logs`);
      const logs = pickArray<any>(body?.User ?? body, ["logs", "items", "rows"]);
      return { User: { logs } } as any;
    },
    enabled: (options.enabled ?? true) && !!userId,
    staleTime: 1000 * 60 * 5,
  });

export interface IAuditEntry {
  id: number;
  user_modified: number;
  modified_by: number;
  /**
   * Nomes resolvidos pelo backend (spec 006, contracts/audit.md). Opcionais de
   * propósito: enquanto o join não chega, a tela cai no id — campo ausente não
   * pode quebrar a trilha.
   */
  modified_by_name?: string | null;
  user_modified_name?: string | null;
  action: string;
  old_values: unknown;
  new_values: unknown;
  ip_address: string | null;
  user_agent: string | null;
  scope_type: string | null;
  scope_id: number | null;
  created_at: string;
}

export interface IAuditPage {
  data: IAuditEntry[];
  pagination: { total: number; totalPages: number };
}

/**
 * As duas trilhas de auditoria só divergem na URL e na chave de cache — mesma
 * resposta, mesma política de erro. Uma função só evita que a de hospital
 * nasça com tratamento diferente da de usuário.
 */
const useAuditQuery = (
  url: string,
  queryKey: readonly unknown[],
  params: { page: number; limit: number },
  enabled: boolean,
  throwOnForbidden = true,
) =>
  useQuery({
    queryKey,
    queryFn: () => apiGet<IAuditPage>(url, { params }),
    enabled,
    staleTime: 1000 * 60,
    // Só o 403 sobe: é o caso que o boundary resolve (redireciona, como o
    // guard). Falha de rede/500 fica na tela, com "tentar de novo" — o retry
    // do boundary não refaz a busca, relançaria o mesmo erro no render.
    // Em nenhum dos dois a tabela pode dizer "nenhum registro".
    // A tab of the user page keeps its 403 (spec 20261028-132000 AC-8): the rest of the page is still readable.
    throwOnError: throwOnForbidden ? isForbidden : false,
    retry: false,
  });

/**
 * GET /audit/users/:id — trilha do que aconteceu COM o usuário (quem mudou o
 * quê, de onde, sob qual escopo). Não confundir com `useUserLogsQuery`, que é a
 * atividade FEITA pelo usuário.
 */
export const useUserAuditQuery = (
  userId: number | undefined,
  params: { page: number; limit: number },
  options: IUseQueryOptions & { throwOnForbidden?: boolean } = {},
) =>
  useAuditQuery(
    `/audit/users/${userId}`,
    queryKeys.users.audit(userId ?? 0, params),
    params,
    (options.enabled ?? true) && !!userId,
    options.throwOnForbidden ?? true,
  );

/**
 * GET /audit/hospitals/:id — a MESMA trilha filtrada pelo escopo do hospital
 * (FR-008): tudo que foi feito sob aquele hospital, de qualquer usuário-alvo.
 * Backend: `audit.routes.ts`, gate `users:manage` papéis [1,2].
 */
export const useHospitalAuditQuery = (
  hospitalId: number | undefined,
  params: { page: number; limit: number },
  options: IUseQueryOptions = {},
) =>
  useAuditQuery(
    `/audit/hospitals/${hospitalId}`,
    queryKeys.hospitals.audit(hospitalId ?? 0, params),
    params,
    (options.enabled ?? true) && !!hospitalId,
  );

/** GET /users/logs — logs globais paginados (Statitic). */
export const useLogsAllQuery = (
  params: IGetLogsAllParams,
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.logs(params),
    queryFn: () => apiGet<IGetLogsAllResponse>(`/users/logs`, { params }),
    enabled: options.enabled ?? true,
    staleTime: 1000 * 60 * 1,
  });

/** GET /users/doctors — busca no cadastro de médicos por CRM/UF (ApproveDoctor). */
export const useUserDoctorQuery = (
  crm: string | undefined,
  uf: string | undefined,
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.doctorByCrm(crm ?? "", uf ?? ""),
    queryFn: () =>
      apiGet<IGetUserDoctorResponse>(`/users/doctors`, {
        params: { crm, uf },
      }),
    enabled: (options.enabled ?? true) && !!crm && !!uf,
    staleTime: 1000 * 60 * 5,
  });

/** GET /users/by-group/:groupId — usuários por grupo de hospitais (GroupProfile). */
export const useUsersByGroupQuery = (
  groupId: string | undefined,
  params: Record<string, unknown> = {},
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.byGroup(groupId ?? "", params),
    queryFn: () => apiGet<any>(`/users/by-group/${groupId}`, { params }),
    enabled: (options.enabled ?? true) && !!groupId,
    staleTime: 1000 * 60 * 5,
  });

/** GET /users/by-hospital/:hospitalId — usuários por hospital (MembersGroup). */
export const useUsersByHospitalQuery = (
  hospitalId: string | undefined,
  params: Record<string, unknown> = {},
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.byHospital(hospitalId ?? "", params),
    queryFn: () =>
      apiGet<AxiosResponse<any>>(`/users/by-hospital/${hospitalId}`, { params }),
    enabled: (options.enabled ?? true) && !!hospitalId,
    staleTime: 1000 * 60 * 5,
  });

/** GET /users — lookup por email (ChangeActiveHospitalModal). */
export const useUsersByEmailQuery = (
  params: IGetUserByEmailAndHospitalParams | undefined,
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.byEmail(params),
    queryFn: () =>
      apiGet<IGetUserByEmailAndHospitalResponse[]>("/users", { params }),
    enabled: (options.enabled ?? true) && !!params?.email,
    staleTime: 1000 * 60 * 5,
  });

/**
 * GET /users (lista paginada com filtros) — hook usado pela página Users.
 * Mantém shape de retorno legado: `[users[], total]`. Não confundir com
 * `useUsersListQuery` (lista simples sem paginação).
 */
export const useUsersByHospitalsUserQuery = (
  filters: IGetUsersByHospitalsUserParams,
  options: IUseQueryOptions = {},
) =>
  useQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: async () => {
      const apiParams: Record<string, string | number> = {
        page: filters.currentPage,
        perPage: filters.totalPerPage,
      };
      if (filters.name) apiParams.name = filters.name;
      if (filters.hospital) apiParams.hospitalId = filters.hospital;
      if (filters.status !== undefined && filters.status !== "")
        apiParams.status = filters.status;
      if (filters.role !== undefined && filters.role !== "")
        apiParams.role = filters.role;

      const payload = await apiGet<{
        users: IGetUsersByHospitalsUserResponse[0][];
        pagination: {
          total: number;
          page: number;
          perPage: number;
          totalPages: number;
        };
      }>(`/users`, { params: apiParams });
      return [payload.users, payload.pagination.total] as unknown as
        | IGetUsersByHospitalsUserResponse
        | undefined;
    },
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
